import React from "react";
import {
  Grid,
  Button,
  Popover,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import styled from "styled-components";
import ExtjsCodeMirror from "app/common/components/input/ExtjsCodeMirror";
import Menu from "app/common/components/input/Menu/Menu";
import { MdExpandMore } from "react-icons/md";

export default function ExpressionFormulaPopup({
  anchorEl,
  onClose,
  onSave,
  formula: savedFormula = "",
  columnName,
  validateExpressionFormula,
  columnTypeId,
  store,
}) {
  const editorRef = React.useRef();
  const [formula, setFormula] = React.useState(savedFormula);
  const [isValidating, setValidating] = React.useState(false);
  const variables = store.getSelectableColumnsForExpressions();

  const addVariableToBody = (value) =>
    editorRef.current && editorRef.current.insertAtCursor(value);
  const handleSave = () => {
    const isSheetVariable = variables.records.find(
      (col) => col.alias_path === formula.trim()
    );
    if (isSheetVariable) {
      Ext.Msg.alert("Error", "Expression cannot be same as the sheet column.");
      return;
    }
    onSave(formula);
  };
  const handleTestSyntaxClick = async () => {
    setValidating(true);
    await validateExpressionFormula(formula);
    setValidating(false);
  };

  return (
    <PopoverStyled
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorEl={anchorEl}
      fullWidth
      maxWidth="md"
    >
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            Enter Formula.
            <ExtjsCodeMirror
              ref={editorRef}
              formula={savedFormula}
              onChange={setFormula}
              columnName={columnName}
              mode="text/x-mysql"
              columnTypeId={columnTypeId}
              // rows="30"
              // cols="50"
              // grow={true}
              // fieldLabel="Expression"
              columns={variables.records}
              emptyText={
                "x - y\r\n x + y\r\n concat(x, y, x)\r\n year(date)\r\n"
              }
              // columns={this.selectableColumns.records}
              name="alias_path"
            />
            <Grid item container>
              <StyledButton
                variant="outlined"
                onClick={handleTestSyntaxClick}
                disabled={isValidating}
              >
                {isValidating ? <CircularProgress size={18} /> : "Test Syntax"}
              </StyledButton>
              <ColumnsMenu
                columns={variables.records}
                onMenuItemClick={(i) => addVariableToBody(i.alias_path)}
              />
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <VariableTabs variables={variables} onClick={addVariableToBody} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} color="primary">
          Save
        </Button>
      </DialogActions>
    </PopoverStyled>
  );
}

function VariableTabs({ variables, onClick }) {
  const [value, setValue] = React.useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  let statements = [
    {
      description: "IF(Condition, Expression1, Expression2)",
      value: "IF( , , )",
    },
    {
      description: "CONCAT(TextField1/Text1, Text2, ...)",
      value: "CONCAT( , )",
    },
    {
      description: "STRCMP(TextField1/Text1, Text12)",
      value: "STRCMP()",
    },
    {
      description: "SUBSTR(TextField/Text, Offset, Length)",
      value: "SUBSTR()",
    },
    {
      description: "REPLACE(TextField1/Text, FromText, ToText)",
      value: "REPLACE( ,  , )",
    },
    {
      description: "DATE(DateField)",
      value: "DATE()",
    },
    {
      description: "YEAR(DateField)",
      value: "YEAR()",
    },
  ];

  if (false) {
    statements = statements.concat(
      {
        description: "SUM(NumberField/CurrencyField)",
        value: "SUM()",
      },
      {
        description: "MAX(NumberField/CurrencyField)",
        value: "MAX()",
      },
      {
        description: "MIN(NumberField/CurrencyField)",
        value: "MIN()",
      },
      {
        description: "AVG(NumberField/CurrencyField)",
        value: "AVG()",
      },
      {
        description: "COUNT(Field)",
        value: "COUNT()",
      }
    );
  }

  return (
    <>
      <Tabs
        value={value}
        onChange={handleChange}
        indicatorColor="primary"
        textColor="primary"
      >
        <StyledTab label="Variables" />
        <StyledTab label="Functions/Constructs" />
      </Tabs>
      <TabPanel value={value} index={0}>
        (click to select)
        <StyledVariablesList>
          {variables.records.map((variable) => (
            <StyledVariable
              component={"a"}
              onClick={() => onClick(variable.alias_path)}
            >
              {variable.alias_path}
            </StyledVariable>
          ))}
        </StyledVariablesList>
      </TabPanel>
      <TabPanel value={value} index={1}>
        <StyledVariablesList>
          {statements.map((statement) => (
            <StyledVariable
              component={"a"}
              onClick={() => onClick(statement.value)}
            >
              {statement.description}
            </StyledVariable>
          ))}
        </StyledVariablesList>
      </TabPanel>
    </>
  );
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={1}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function ColumnsMenu({ columns, onMenuItemClick }) {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (e) => {
    handleClose();
    onMenuItemClick(e);
  };

  return (
    <>
      <StyledButton
        aria-haspopup="true"
        onClick={handleClick}
        variant="outlined"
        endIcon={<MdExpandMore />}
      >
        Insert a variable...
      </StyledButton>
      <Menu
        open={Boolean(anchorEl)}
        menuItems={columns}
        anchorElement={anchorEl}
        onClose={handleClose}
        valueKey={"alias_path"}
        onMenuItemClick={handleMenuItemClick}
      />
    </>
  );
}

const StyledButton = styled(Button)`
  /* display: block; */
  && {
    margin: 5px;
  }
`;

const PopoverStyled = styled(({ className, ...props }) => (
  <Popover {...props} classes={{ paper: className }} />
))`
  && {
    max-width: 700px;
    width: 100%;
  }
`;

const StyledTab = styled(Tab)`
  /* min-width: 50px; */
`;

const StyledVariable = styled(Button)`
  && {
    border: 1px solid #eae9eb;
    margin: 5px 5px 0 0;
    border-radius: 5px;
    text-align: center;
  }
`;

const StyledVariablesList = styled.div`
  max-height: 300px;
  overflow: auto;
`;
